import Button from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { useNotify } from "ra-core";
import React, { cloneElement, useEffect, useState } from "react";
import {
  Create,
  CreateButton,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  List,
  sanitizeListRestProps,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  useTranslate,
} from "react-admin";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import { LoopbackAPI } from "../react-admin-loopback/api/loopback-api";

export const AppVersionList = (props) => {
  const translate = useTranslate();

  return (
    <List
      {...props}
      actions={<ListActions />}
      sort={{ field: "updatedAt", order: "DESC" }}
    >
      <Datagrid>
        <TextField source="version" />
        <TextField source="buildNumber" />
        <DateField
          source="forceUpdateDate"
          label={translate("appversion.forceUpdateDate")}
          locales="en-US"
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            timeZone: "UTC",
          }}
        />
      </Datagrid>
    </List>
  );
};

const ListActions = (props) => {
  const { className, exporter, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    hasCreate,
    basePath,
    selectedIds,
    showFilter,
    total,
  } = useListContext();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}
      <CreateButton className="mt-1" basePath={basePath} />
      <CustomSendNotifyButton />
    </TopToolbar>
  );
};

const CustomSendNotifyButton = (props) => {
  const [show, setShow] = useState(false);
  const notify = useNotify();
  const sendNotify = async () => {
    const API = new LoopbackAPI();
    try {
      await API.appVersions.appVersionRemindNewVersion();
      notify("Success", "success");
    } catch (error: any) {
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
          ? "ra.auth.sign_in_error"
          : error.message,
        "warning"
      );
    }
  };
  return (
    <Button
      onClick={() => sendNotify()}
      color="primary"
      startIcon={<NotificationsIcon />}
    ></Button>
  );
};

const CustomForm = (props) => {
  useTranslate();
  const [appUpdateDays, setAppUpdateDays] = useState<number>(14);
  const notify = useNotify();
  // Initialize with calculated default date
  const initialDefaultDate = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextUpdateDate = new Date(
      today.getTime() + (14 + 1) * 24 * 60 * 60 * 1000
    );
    return nextUpdateDate.toISOString().split("T")[0];
  })();
  const [defaultDate, setDefaultDate] = useState<string>(initialDefaultDate);

  // Calculate the next update date (end of today + appUpdateDays)
  const calculateNextUpdateDate = (days: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // End of today
    const nextUpdateDate = new Date(
      today.getTime() + (days + 1) * 24 * 60 * 60 * 1000
    );
    return nextUpdateDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  useEffect(() => {
    const fetchAppUpdateSettings = async () => {
      const API = new LoopbackAPI();

      try {
        const filter = JSON.stringify({ where: { nom: "app_update_notice" } });
        const settingsData = await API.settings.find({ filter });

        if (settingsData?.length > 0) {
          const days = settingsData[0].scheduledDay || 14;
          setAppUpdateDays(days);
          setDefaultDate(calculateNextUpdateDate(days)); // Use the new days value directly
        } else {
          setDefaultDate(calculateNextUpdateDate(14));
        }
      } catch (error: any) {
        const errorMessage =
          typeof error === "string"
            ? error
            : !error?.message
            ? "Error fetching settings"
            : error.message;

        notify(errorMessage, "warning");
      }
    };

    fetchAppUpdateSettings();
  }, [notify]);

  // Update default date when appUpdateDays changes
  useEffect(() => {
    setDefaultDate(calculateNextUpdateDate(appUpdateDays));
  }, [appUpdateDays]);

  return (
    <div className="w-100">
      <div className="d-flex flex-wrap">
        <div className="col-12 col-sm-6">
          <TextInput source="version" fullWidth />
        </div>
        <div className="col-12 col-sm-6">
          <TextInput source="buildNumber" fullWidth />
        </div>
        <div className="col-12 col-sm-6">
          <DateInput
            source="forceUpdateDate"
            label="Forced Update Deadline"
            fullWidth
            defaultValue={defaultDate}
            minValue={(() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              tomorrow.setHours(0, 0, 0, 0);
              return tomorrow.toISOString().split("T")[0];
            })()}
            format={(value) => {
              return value ? value.split("T")[0] : "";
            }} // Display just the date part
            validate={[
              (value) => {
                if (!value) return undefined;
                const today = new Date();
                today.setHours(23, 59, 59, 999); // End of current day
                const selectedDate = new Date(value);
                // Check if selected date is today or earlier
                if (selectedDate <= today) {
                  return "Date passée non autorisée";
                }
                return undefined;
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export const AppVersionCreate = (props: any) => {
  const translate = useTranslate();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <BackButton />
        <h3>{translate("appversion.create")}</h3>
        <b></b>
      </div>
      <Create {...props}>
        <SimpleForm redirect="/AppVersions">
          <CustomForm />
        </SimpleForm>
      </Create>
    </div>
  );
};

export const AppVersionEdit = (props: any) => {
  const translate = useTranslate();

  // Transform data before submission to ensure forceUpdateDate is set to end of day
  const transform = (data: any) => {
    console.log("Form data before transform:", data);
    if (data.forceUpdateDate) {
      // Convert date to end of day
      const date = new Date(data.forceUpdateDate);
      date.setHours(0, 0, 0, 0);
      data.forceUpdateDate = date.toISOString();
      console.log("Form data after transform:", data);
    }
    return data;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <Link to="/Parametres" className="text-decoration-none">
          <ArrowBackIcon /> {translate("button.back")}
        </Link>
        <h3>{translate("guide.edit")}</h3>
        <b></b>
      </div>
      <Edit {...props} transform={transform}>
        <SimpleForm>
          <CustomForm />
        </SimpleForm>
      </Edit>
    </div>
  );
};
